/**
 * Helpers pour ouvrir/fermer le Compose et envoyer le message.
 *
 * - openNewCompose()   : modale vide pour un nouveau mail
 * - openReplyCompose() : pré-rempli pour répondre à un message
 * - closeCompose()     : ferme la modale
 * - sendDraft()        : build RFC 822 + sendMessage Gmail
 */

import { settingsState } from "../settings.svelte";
import { gmailState, type ComposeDraft } from "./store.svelte";
import { authState } from "../auth/store.svelte";
import { getHeader, parseFrom, sendMessage, type GmailMessage } from "./api";
import { buildMessage, base64UrlEncodeString } from "./mime";
import { extractBody } from "./parser";
import { getSignature } from "./signature";
import { loadInbox } from "./sync";
import { getAccessToken } from "../auth/oauth";
import { mePixelsPost, getWorkerUrl } from "../workerClient";

function emptyDraft(): ComposeDraft {
  return {
    to: "",
    cc: "",
    bcc: "",
    subject: "",
    body: "",
    showCcBcc: false,
    // Le pixel de suivi est la raison d'être de Mailfox, donc actif par
    // défaut — mais le réglage peut l'éteindre.
    track: settingsState.trackByDefault,
    attachments: [],
  };
}

export function openNewCompose(): void {
  gmailState.composeDraft = emptyDraft();
}

/**
 * Forward d'un message. Pré-remplit le subject avec "Fwd: ..." et le body
 * avec un blanc + une citation du message original.
 *
 * Différent du reply : pas de threading (In-Reply-To/References/threadId)
 * → forward démarre une nouvelle conversation pour le destinataire.
 */
export function openForwardCompose(message: GmailMessage): void {
  const subjectRaw = getHeader(message, "Subject") ?? "";
  const subject = /^(fwd|tr):/i.test(subjectRaw.trim())
    ? subjectRaw
    : `Fwd: ${subjectRaw}`;

  gmailState.composeDraft = {
    to: "",
    cc: "",
    bcc: "",
    subject,
    body: "",
    showCcBcc: false,
    track: settingsState.trackByDefault,
    attachments: [],
    // Pas de threading volontairement — forward = nouvelle conversation
  };

  // Pour la citation : on stocke un flag pour que sendDraft sache qu'il faut
  // citer ce message. Plus simple : passer par threadId mais sans envoyer comme
  // reply. Hack : on met threadId pour que getReplyQuote trouve le message
  // (currentThread est encore loadé), mais on PURGE threadId avant le sendMessage.
  // → Trop fragile. Approche meilleure : on cache le forwardSourceMessageId.
  // Pour Phase 5b MVP, on passe par cacheForwardQuote ci-dessous.
  cacheForwardQuote(message);
}

/**
 * Cache la citation du message forwardé. Lue par sendDraft pour l'inclure.
 * Module-level state, valide jusqu'au prochain openX (auto-clear par
 * closeCompose).
 */
let pendingForwardQuote: { html: string; text: string } | null = null;

function cacheForwardQuote(message: GmailMessage): void {
  const fromHeader = getHeader(message, "From");
  const { name, email } = parseFrom(fromHeader);
  const date = parseInt(message.internalDate, 10);
  const body = extractBody(message);
  const plainText =
    body.text?.trim() ||
    (body.html
      ? new DOMParser().parseFromString(body.html, "text/html").body
          .textContent || ""
      : "");
  pendingForwardQuote = buildForwardQuote(name, email, date, plainText);
}

function buildForwardQuote(
  name: string,
  email: string,
  unixMs: number,
  plainText: string,
): { html: string; text: string } {
  const dateStr = new Date(unixMs).toLocaleString("fr-FR", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  const escape = (s: string) =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const sender = name ? `${name} <${email}>` : email;
  const text =
    `---------- Message transféré ----------\n` +
    `De: ${sender}\n` +
    `Date: ${dateStr}\n\n` +
    plainText;
  const html =
    `<div class="gmail_quote">` +
    `<div>---------- Message transféré ----------</div>` +
    `<div>De: ${escape(sender)}</div>` +
    `<div>Date: ${escape(dateStr)}</div>` +
    `<br>` +
    `<div>${escape(plainText).replace(/\r?\n/g, "<br>")}</div>` +
    `</div>`;
  return { html, text };
}

/** Pré-remplit pour répondre à `message`. Le subject reçoit "Re: " si absent. */
export function openReplyCompose(message: GmailMessage, threadId: string): void {
  const fromHeader = getHeader(message, "From");
  const { name, email } = parseFrom(fromHeader);
  const subjectRaw = getHeader(message, "Subject") ?? "";
  const subject = /^re:/i.test(subjectRaw.trim())
    ? subjectRaw
    : `Re: ${subjectRaw}`;
  const messageId = getHeader(message, "Message-ID") ?? "";
  const refsHeader = getHeader(message, "References") ?? "";
  const references = [
    ...refsHeader.split(/\s+/).filter(Boolean),
    messageId,
  ].filter(Boolean);

  gmailState.composeDraft = {
    to: name ? `${name} <${email}>` : email,
    cc: "",
    bcc: "",
    subject,
    body: "",
    showCcBcc: false,
    track: settingsState.trackByDefault,
    attachments: [],
    inReplyTo: messageId || undefined,
    references: references.length > 0 ? references : undefined,
    threadId,
    replyToFromLabel: name?.trim() || email,
  };
}

export function closeCompose(): void {
  gmailState.composeDraft = null;
  pendingForwardQuote = null; // clear le forward cache
}

// ─── Send ────────────────────────────────────────────────────────────────────

export interface SendResult {
  ok: boolean;
  error?: string;
}

/**
 * Parse "Nom <email@x>, autre@y, Trois <three@z>" → ["Nom <email@x>", ...]
 * Tolérant : split sur virgule ou ; trim, filter empty.
 */
function parseRecipients(input: string): string[] {
  return input
    .split(/[,;]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Cap Gmail : 25 MB total raw (avant base64 → ~33 MB encoded). */
export const MAX_ATTACHMENT_TOTAL_BYTES = 25 * 1024 * 1024;

/** Validation basique : au moins 1 destinataire, format email reconnaissable */
function validate(draft: ComposeDraft): string | null {
  const to = parseRecipients(draft.to);
  if (to.length === 0) return "Destinataire manquant";
  for (const r of to) {
    // Tolère "Nom <email@x>" ou "email@x"
    const emailPart = r.includes("<") ? r.split("<")[1]?.split(">")[0] : r;
    if (!emailPart || !emailPart.includes("@")) {
      return `Email invalide : ${r}`;
    }
  }
  if (!draft.subject.trim() && !draft.body.trim() && draft.attachments.length === 0) {
    return "Sujet, corps et pièces jointes vides";
  }
  // Cap attachments
  const total = draft.attachments.reduce((s, a) => s + a.size, 0);
  if (total > MAX_ATTACHMENT_TOTAL_BYTES) {
    const mb = (total / 1024 / 1024).toFixed(1);
    return `Pièces jointes trop volumineuses (${mb} MB > 25 MB)`;
  }
  return null;
}

// ─── Attachments helpers ────────────────────────────────────────────────────

/** Lit un File → DraftAttachment (read async, encode bytes en mémoire). */
export async function fileToAttachment(file: File): Promise<{
  uid: string;
  filename: string;
  mimeType: string;
  size: number;
  bytes: Uint8Array<ArrayBuffer>;
}> {
  const buffer = await file.arrayBuffer();
  return {
    uid: crypto.randomUUID(),
    filename: file.name,
    mimeType: file.type || "application/octet-stream",
    size: file.size,
    bytes: new Uint8Array(buffer) as Uint8Array<ArrayBuffer>,
  };
}

export function addAttachments(files: File[]): { added: number; skipped: string[] } {
  const draft = gmailState.composeDraft;
  if (!draft) return { added: 0, skipped: [] };

  // Charge tous les files en mémoire en parallèle
  const skipped: string[] = [];
  Promise.all(files.map(fileToAttachment))
    .then((attachments) => {
      const currentTotal = draft.attachments.reduce((s, a) => s + a.size, 0);
      let runningTotal = currentTotal;
      for (const a of attachments) {
        if (runningTotal + a.size > MAX_ATTACHMENT_TOTAL_BYTES) {
          skipped.push(a.filename);
          continue;
        }
        runningTotal += a.size;
        draft.attachments = [...draft.attachments, a];
      }
    })
    .catch((e) => console.error("addAttachments failed:", e));

  return { added: files.length - skipped.length, skipped };
}

export function removeAttachment(uid: string): void {
  const draft = gmailState.composeDraft;
  if (!draft) return;
  draft.attachments = draft.attachments.filter((a) => a.uid !== uid);
}

/**
 * Construit la citation HTML + plain text pour un reply.
 * Format Gmail-style : "On <date>, <name> <email> wrote:\n> body lines"
 */
function buildReplyQuote(
  originalFromName: string,
  originalFromEmail: string,
  originalDate: number,
  originalText: string,
): { html: string; text: string } {
  const dateStr = new Date(originalDate).toLocaleString("fr-FR", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  const senderLine = originalFromName
    ? `Le ${dateStr}, ${originalFromName} <${originalFromEmail}> a écrit :`
    : `Le ${dateStr}, ${originalFromEmail} a écrit :`;

  const text =
    senderLine +
    "\n" +
    originalText
      .split(/\r?\n/)
      .map((l) => "> " + l)
      .join("\n");

  // HTML version : wrap dans gmail_quote pour que les destinataires Gmail
  // identifient et collapsent automatiquement
  const escapedSenderLine = senderLine
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  const escapedText = originalText
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\r?\n/g, "<br>");
  const html =
    `<div class="gmail_quote">` +
    `<div>${escapedSenderLine}</div>` +
    `<blockquote class="gmail_quote" ` +
    `style="margin:0 0 0 .8ex;border-left:1px #ccc solid;padding-left:1ex">` +
    escapedText +
    `</blockquote></div>`;

  return { html, text };
}

/**
 * Récupère le message original depuis le thread courant pour reconstruire
 * la citation au moment du send. On le fait dynamiquement pour ne pas
 * stocker le body (potentiellement gros) dans le ComposeDraft.
 */
function getReplyQuote(
  threadId: string,
  inReplyToHeader?: string,
): { html: string; text: string } | null {
  const t = gmailState.currentThread;
  if (!t || t.id !== threadId) return null;
  // Trouve le message qu'on cite : celui dont le Message-ID match inReplyTo,
  // sinon le dernier message du thread (= le plus récent vu par l'user)
  const target =
    t.messages.find(
      (m) => getHeader(m, "Message-ID") === inReplyToHeader,
    ) ?? t.messages[t.messages.length - 1];
  if (!target) return null;
  const fromHeader = getHeader(target, "From");
  const { name, email } = parseFrom(fromHeader);
  const date = parseInt(target.internalDate, 10);
  const body = extractBody(target);
  const plainText =
    body.text?.trim() ||
    (body.html
      ? new DOMParser().parseFromString(body.html, "text/html").body
          .textContent || ""
      : "");
  return buildReplyQuote(name, email, date, plainText);
}

/** UUID v4 simple via crypto.randomUUID (dispo Safari 15.4+, OK pour iOS 16.4+) */
function newPixelId(): string {
  return crypto.randomUUID();
}

/**
 * Envoie le draft. Build RFC 822 + base64url + POST Gmail.
 * Si track activé, génère un pixel_id, l'injecte dans le HTML body, et POST
 * `/me/pixels` au Worker en parallèle (registry pour le push notif).
 * Refresh inbox au succès. Retourne {ok, error?}.
 */
export async function sendDraft(): Promise<SendResult> {
  const draft = gmailState.composeDraft;
  if (!draft) return { ok: false, error: "Pas de draft à envoyer" };

  const err = validate(draft);
  if (err) return { ok: false, error: err };

  const fromEmail = authState.email;
  if (!fromEmail) return { ok: false, error: "Pas signé in" };

  // Récupère la signature (cache instant si disponible)
  let signatureHtml = "";
  try {
    signatureHtml = await getSignature();
  } catch (e) {
    console.warn("getSignature failed, sending without:", e);
  }

  // Citation pour reply (si threadId set) OU pour forward (cached)
  let quoteHtml: string | undefined;
  let quoteText: string | undefined;
  if (draft.threadId) {
    const q = getReplyQuote(draft.threadId, draft.inReplyTo);
    if (q) {
      quoteHtml = q.html;
      quoteText = q.text;
    }
  } else if (pendingForwardQuote) {
    quoteHtml = pendingForwardQuote.html;
    quoteText = pendingForwardQuote.text;
  }

  // === Tracking pixel =====================================================
  // Si activé : génère pixel_id, construit l'URL (Worker URL + path), injecte
  // dans HTML body. POST /me/pixels parallèle au sendMessage pour économiser
  // ~100ms. Si /me/pixels échoue → log warning, continue le send (le mail
  // partira quand même, juste pas de push notif pour les opens).
  const recipients = parseRecipients(draft.to);
  let pixelId: string | undefined;
  let pixelUrl: string | undefined;
  let registerPromise: Promise<unknown> | undefined;

  if (draft.track) {
    pixelId = newPixelId();
    pixelUrl = `${getWorkerUrl()}/p/${pixelId}.gif`;
    const accessToken = await getAccessToken();
    if (accessToken) {
      // Pour le registry : 1ʳᵉ adresse parsée comme recipient principal.
      // Si plusieurs destinataires, on join (= mode aggregate, comme desktop).
      const recipientEmails = recipients.map((r) => {
        const e = r.includes("<") ? r.split("<")[1]?.split(">")[0] : r;
        return (e || r).trim();
      });
      const recipientName = recipients[0]?.includes("<")
        ? recipients[0].split("<")[0].trim().replace(/^"|"$/g, "") || null
        : null;
      registerPromise = mePixelsPost(accessToken, {
        pixel_id: pixelId,
        recipient_email: recipientEmails.join(", "),
        recipient_name: recipientName,
        subject: draft.subject || null,
        sent_at: Math.floor(Date.now() / 1000),
      }).catch((e) => {
        console.warn("POST /me/pixels failed (sending anyway):", e);
      });
    }
  }

  const rfc822 = buildMessage(
    {
      to: recipients,
      cc: parseRecipients(draft.cc),
      bcc: parseRecipients(draft.bcc),
      subject: draft.subject,
      bodyText: draft.body,
      signatureHtml: signatureHtml || undefined,
      quoteHtml,
      quoteText,
      inReplyTo: draft.inReplyTo,
      references: draft.references,
      trackingPixelUrl: pixelUrl,
      attachments: draft.attachments.map((a) => ({
        filename: a.filename,
        mimeType: a.mimeType,
        bytes: a.bytes,
      })),
    },
    fromEmail,
  );

  const raw = base64UrlEncodeString(rfc822);

  try {
    // Send + register en parallèle (registerPromise est déjà en flight)
    const [sendRes] = await Promise.all([
      sendMessage(raw, draft.threadId),
      registerPromise ?? Promise.resolve(),
    ]);
    void sendRes;
  } catch (e: unknown) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : String(e),
    };
  }

  // Succès → ferme le compose + refresh inbox pour voir le sent thread
  closeCompose();
  void loadInbox();
  return { ok: true };
}
